import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FEATURE_PROGRESS,
  type ShowoffItemFormatted,
  type StepType,
  type DashboardItem,
  type FeatureProgressType,
  type ShowoffElementType,
} from '../types';
import structure from '../helpers/structure';
import createTree from '../helpers/createTree';
import { useCurrentSelected } from './useCurrentSelected';
import {
  getShowoffValue,
  setShowoffValue,
  isStorageAccessible,
} from '../helpers/showoffStorage';
import {
  trackShowoffViewed,
  trackShowoffSelected,
  trackShowoffClosed,
} from '../helpers/showoffTracking';

import { setShowoff } from '@/store/actions';
import { useStore } from '@/store/useStore';

export interface UseShowoffResult {
  elemRef: ReturnType<typeof useCurrentSelected>['currentSelected'];
  action: () => void;
  actionDismiss: () => void;
}

/**
 * Feature highlight / onboarding hook.
 *
 * Tracks which features a user has seen (via localStorage or wrapper prefs)
 * and drives the overlay-based "show off" flow that walks the user through
 * new UI elements.
 *
 * @param dashboardItems – optional list of dashboard-level items whose
 *   visibility should gate the show-off flow.  When omitted the hook
 *   skips dashboard-item visibility checks.
 */
const useShowoff = (dashboardItems?: DashboardItem[]) => {
  const isAndroid = useStore((s) => s.runtime.wrapperType.isAndroid);
  const gameLoadSuccess = useStore((s) => s.gameStatus.gameLoadSuccess);
  const gameWindowOpen = useStore((s) => s.gameWindowContext.gameWindowOpen);

  const showedOff = getShowoffValue(isAndroid);
  const showedOffArray = useMemo(
    () => showedOff?.split(' ') ?? [],
    [showedOff],
  );

  const initializedRef = useRef(false);
  const [toShow, setToShow] = useState<ShowoffItemFormatted | null>(null);

  const {
    waitForDismiss,
    setWaitForDismiss,
    applyNotificationsAndBadges,
    currentSelected,
    setCurrentSelected,
    checkBypassOnNext,
    inView,
    hasBeenViewed,
  } = useCurrentSelected({ toShow, setToShow });

  const wasGameWindowOpenRef = useRef(false);

  const pendingShow = useMemo(() => {
    if (!gameLoadSuccess || !isStorageAccessible(showedOff)) {
      return null;
    }

    const steps: StepType[] = [];
    const candidates: ShowoffItemFormatted[] = [];
    const highestOrder = { order: 1 };

    const storedFeatures = showedOffArray.reduce(
      (acc, val) => {
        const [feature, flow] = val.split('~');
        if (feature && flow) {
          return { ...acc, [flow]: [...(acc[flow] ?? []), feature] };
        }
        return acc;
      },
      {
        [FEATURE_PROGRESS.FULL]: [] as string[],
        [FEATURE_PROGRESS.NOTICED]: [] as string[],
      },
    );

    const featuresMap = new Map([
      [FEATURE_PROGRESS.FULL, storedFeatures[FEATURE_PROGRESS.FULL]],
      [FEATURE_PROGRESS.NOTICED, storedFeatures[FEATURE_PROGRESS.NOTICED]],
    ]) as Map<FeatureProgressType, ShowoffElementType[]>;

    createTree(
      structure,
      featuresMap,
      candidates,
      steps,
      highestOrder,
      dashboardItems,
    );

    let maxIndex = 0;
    let maxOrder = 0;

    candidates.forEach((item, index) => {
      if ((item.order ?? 0) > maxOrder) {
        maxOrder = item.order ?? 0;
        maxIndex = index;
      }
    });

    if (maxOrder < highestOrder.order) {
      return null;
    }

    return [candidates[maxIndex]];
  }, [dashboardItems, gameLoadSuccess, showedOff, showedOffArray]);

  const markNoticed = useCallback(() => {
    setToShow((prev) => {
      if (!prev) return null;
      const prevStorage =
        showedOff && showedOff.length > 0 ? `${showedOff} ` : '';
      setShowoffValue(
        `${prevStorage}${prev.name}~${FEATURE_PROGRESS.NOTICED}`,
        isAndroid,
      );
      return null;
    });
    initializedRef.current = false;
  }, [showedOff, isAndroid]);

  const markFull = useCallback(() => {
    setToShow((prev) => {
      if (!prev) return null;
      let prevStorage =
        showedOff && showedOff.length > 0 ? `${showedOff} ` : '';

      if (prevStorage.length > 0) {
        const features = prevStorage.split(' ');
        prevStorage = features
          .filter((item) => !item.includes(prev.name))
          .join(' ');
      }

      setShowoffValue(
        `${prevStorage}${prev.name}~${FEATURE_PROGRESS.FULL}`,
        isAndroid,
      );

      setShowoff({ feature: null, currentStep: null });
      return null;
    });
    setCurrentSelected(null);
  }, [setCurrentSelected, showedOff, isAndroid]);

  useEffect(() => {
    const wasOpen = wasGameWindowOpenRef.current;
    wasGameWindowOpenRef.current = !!gameWindowOpen;

    if (!toShow || !hasBeenViewed) return;

    const gameWindowClosed = wasOpen && !gameWindowOpen;
    const highlightedElementLeftView = waitForDismiss && !inView;

    if (gameWindowClosed || highlightedElementLeftView) {
      applyNotificationsAndBadges(true);
      requestAnimationFrame(() => {
        setWaitForDismiss(false);
        markFull();
      });
    }
  }, [
    applyNotificationsAndBadges,
    gameWindowOpen,
    hasBeenViewed,
    inView,
    markFull,
    setWaitForDismiss,
    toShow,
    waitForDismiss,
  ]);

  useEffect(() => {
    if (!initializedRef.current && pendingShow) {
      if (pendingShow[0].show) {
        trackShowoffViewed();
      }
      initializedRef.current = true;
      setShowoff({
        feature: pendingShow[0],
        name: pendingShow[0].name,
      });
      const feature = pendingShow[0];
      requestAnimationFrame(() => {
        setToShow(() => {
          if (feature.steps && feature.steps.length > 0) {
            const remain = feature.steps.filter(
              (item) => !checkBypassOnNext(item),
            );
            return { ...feature, steps: remain };
          }
          return feature;
        });
      });
    }
  }, [checkBypassOnNext, pendingShow]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      applyNotificationsAndBadges();
    }, 200);

    return () => {
      clearTimeout(timeout);
    };
  }, [applyNotificationsAndBadges, gameWindowOpen]);

  return {
    elemRef: currentSelected,
    action: () => {
      trackShowoffSelected();
      currentSelected?.element?.click();
    },
    actionDismiss: () => {
      trackShowoffClosed();
      markNoticed();
    },
  } satisfies UseShowoffResult;
};

export { useShowoff, type UseShowoffResult as ShowoffHookReturn };
